import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Panel } from '../../../components/Panel'
import { TrendChart } from '../../../components/charts/TrendChart'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { useDeskState } from './useDeskState'
import { Loading, WhoBar, WhoLegend } from './ArticleBits'
import { deskApi, type DeskPub } from '../../../api/visitorsDesk'
import { api, ApiError, type MeResponse } from '../../../api/client'
import { metaLine, mono, td, th } from '../../../lib/deskArticles'
import { deskPath } from '../../../lib/deskTabs'
import { CREDIT_TODAY_NOTE, creditText, rateWithCount } from '../../../lib/deskFormat'
import { fmtInt } from '../../../lib/format'
import { useParams } from 'react-router-dom'

/** `visitors/desk/:pub/articles/:articleId`: one article, one title. */
export function DeskArticle() {
  return <DeskFrame tab="articles"><Body /></DeskFrame>
}

type NamedReader = { subscriber_id: number; name: string | null; last_read_at: string }

/** Named readers. Without member-history access an admin sees why; editors and writers never
 *  have it, so they see no panel at all. */
export function NamedReadersPanel({ readers, isAdmin }: { readers: NamedReader[] | undefined; isAdmin: boolean }) {
  if (readers === undefined && !isAdmin) return null
  return (
    <Panel label="Named readers" accent="none">
      {readers === undefined ? <p className="text-paper-faint">Named readers need member-history access</p> : (
        <ul className="space-y-1 text-[13px]">
          {readers.map((r) => (
            <li key={r.subscriber_id}>
              <Link to={`/members/${r.subscriber_id}`} className="text-amber-glow hover:underline">{r.name ?? `Member ${r.subscriber_id}`}</Link>
              <span className="ml-2 font-mono text-[11px] text-paper-faint">last read {r.last_read_at.slice(0, 10)}</span>
            </li>
          ))}
          {readers.length === 0 && <li className="text-paper-faint">No named readers in this period</li>}
        </ul>
      )}
    </Panel>
  )
}

function Body() {
  const { pub, q, spKey } = useDeskState('articles')
  const meQ = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const me = meQ.data
  const { articleId = '' } = useParams()
  const id = Number(articleId)
  const live = q.period === 'today'
  const query = useQuery({
    queryKey: ['desk', 'article', pub, q.period, q.from, q.to, q.compare, q.group, id],
    queryFn: () => deskApi.article(pub as DeskPub, q, id),
    retry: false, enabled: Number.isInteger(id) && id > 0,
  })
  const back = (
    <Link to={deskPath(pub, 'articles', spKey)} className="font-mono text-[12px] text-amber-glow hover:underline">← All articles</Link>
  )
  if (query.isError) {
    const status = query.error instanceof ApiError ? query.error.status : 0
    return (
      <div className="space-y-3">
        {back}
        <Panel accent="red"><p className="text-paper">{status === 404 ? 'This article is not on this title.' : status === 422 ? 'Pick a single title to see one article.' : 'Couldn’t load the article.'}</p></Panel>
      </div>
    )
  }
  if (!query.data) return <div className="space-y-3">{back}<Loading /></div>
  const d = query.data
  if (d.unavailable !== undefined) return <div className="space-y-3">{back}<NotYetLive text={d.unavailable} /></div>
  const a = d.article
  const t = d.totals
  const days = d.daily.map((x) => x.day)
  return (
    <div className="space-y-4">
      {back}
      <header>
        <h2 className="font-serif text-[24px] leading-tight text-paper">{a.title ?? `Article ${a.article_id}`}</h2>
        <p className="font-mono text-[12px] text-paper-faint"><span className="inline-block max-w-full truncate align-bottom" title={a.path}>{metaLine(a)} · {a.path}</span></p>
        {a.tags.length > 0 && <p className="text-[12px] text-paper-faint">Tags: {a.tags.join(', ')}</p>}
      </header>
      <Panel label="This period" accent="amber">
        {!t ? <p className="text-paper-dim">No reads in this period</p> : (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
              <Stat label="People" value={fmtInt(t.people)} />
              <Stat label="Real reads" value={fmtInt(t.real_reads)} />
              <Stat label="Read to the end" value={rateWithCount(t.read_end_rate, t.read_end)} />
              <Stat label="Read another page" value={t.recirculation_rate === null ? '—' : rateWithCount(t.recirculation_rate, Math.round(t.recirculation_rate * t.views))} />
              <Stat label="Arrived here first" value={fmtInt(t.entries)} />
              <Stat label="Hit paywall" value={rateWithCount(t.paywall_rate, t.paywall_hits)} />
              <Stat label="Members gained" value={creditText(t.members_gained, live)} title={live ? 'credited overnight' : undefined} />
            </dl>
            {live && <p className="font-mono text-[12px] text-paper-faint">{CREDIT_TODAY_NOTE}</p>}
            <WhoLegend />
            <WhoBar who={t.who} />
          </div>
        )}
      </Panel>
      <Panel label="Day by day" accent="cyan">
        {d.daily.length === 0 ? <p className="text-paper-dim">No days to draw</p> : (
          <TrendChart labels={days} height={200}
                      series={[{ label: 'People', values: d.daily.map((x) => x.people), emphasis: true }, { label: 'Real reads', values: d.daily.map((x) => x.real_reads) }]}
                      ariaLabel={`Daily people and real reads for ${a.title ?? 'this article'}, ${days[0]} to ${days[days.length - 1]}`} />
        )}
      </Panel>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel label="Where readers came from" accent="violet">
          <Table head={['Source', 'Visits']} empty="No visits" rows={d.sources.map((s) => [s.label, fmtInt(s.visits)])} />
        </Panel>
        <Panel label="Search terms" accent="green">
          <Table head={['Term', 'Clicks', 'Position']} empty="No search terms" rows={d.search_terms.map((s) => [s.query, fmtInt(s.clicks), s.position === null ? '—' : (Math.round(s.position * 10) / 10).toString()])} />
        </Panel>
        <Panel label="Where they went next" accent="amber">
          <Table head={['Page', 'Visits']} empty="No next pages" rows={d.next_pages.map((s) => [s.path, fmtInt(s.visits)])} />
        </Panel>
      </div>
      <Panel label="Last read before joining" accent="none">
        <p className="text-paper"><span className="font-mono" style={mono}>{fmtInt(d.last_read_before_joining)}</span> {d.last_read_before_joining === 1 ? 'member' : 'members'} joined with this as the last article they read.</p>
      </Panel>
      <NamedReadersPanel readers={d.named_readers} isAdmin={me?.role === 'admin'} />
    </div>
  )
}

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <dt className="font-mono text-[11px] uppercase tracking-wide text-paper-faint">{label}</dt>
      <dd className="font-mono text-[16px] text-paper" style={mono}>{value}</dd>
    </div>
  )
}

function Table({ head, rows, empty }: { head: string[]; rows: string[][]; empty: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead><tr>{head.map((h, i) => <th key={h} className={`${th} ${i > 0 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-ink-600">
              {r.map((c, j) => <td key={j} className={`${td} ${j > 0 ? 'text-right font-mono' : ''}`} style={j > 0 ? mono : undefined}><span className={j === 0 ? 'block max-w-[220px] truncate' : ''} title={j === 0 ? c : undefined}>{c}</span></td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={head.length} className={`${td} text-paper-faint`}>{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  )
}
